<script setup>
import {computed, ref} from "vue";
import * as Sentry from "@sentry/vue";
import {pgettext} from "@/utils/translate";
import {NON_FIELD_ERROR_KEYS} from "@/utils/forms";

const ExceptionType = {
    NETWORK: 'network',
    OTHER: 'other',
};

const props = defineProps({
    fields: {
        type: Object,
    },
    title: {
        type: String,
    },
    nonFieldErrorStep: {
        type: String,
    },
});
const emit = defineEmits(['visitStepsWithErrors']);

function getField(fieldName) {
    if (!props.fields) {
        return null;
    }
    return props.fields[fieldName];
}

const showModal = ref(false);
const exceptionType = ref(null);
const nonFieldErrors = ref(null);
const fieldErrors = ref(null);

function showMutationError(exception, captureException = true) {
    if (captureException) {
        Sentry.captureException(exception);
    }

    exceptionType.value = exception.networkError ? ExceptionType.NETWORK : ExceptionType.OTHER;
    nonFieldErrors.value = null;
    fieldErrors.value = null;
    showModal.value = true;
}

function showValidationError(errorResponse, ignoreFields = false) {
    exceptionType.value = null;
    nonFieldErrors.value = [];
    fieldErrors.value = [];
    for (const error of errorResponse.errors) {
        if (NON_FIELD_ERROR_KEYS.includes(error.field) || ignoreFields) {
            nonFieldErrors.value.push(...error.messages);
        } else {
            const errorObject = {
                field: error.field,
                label: error.field,
                messages: error.messages,
            }
            const field = getField(error.field);
            if (field) {
                errorObject.label = field.label;
                errorObject.step = field.step;
            }

            fieldErrors.value.push(errorObject);
        }
    }

    showModal.value = true;
}

defineExpose({
    showMutationError,
    showValidationError,
});

const dialogTitle = computed(() => {
    return props.title || pgettext('title', 'Error');
});

function confirm() {
    const stepsWithErrors = [];
    const stepsWithErrorsSet = new Set();

    function addStep(step) {
        if (!step) {
            return;
        }
        if (!stepsWithErrorsSet.has(step)) {
            stepsWithErrors.push(step);
            stepsWithErrorsSet.add(step);
        }
    }

    // Visit steps for fields with errors
    if (nonFieldErrors.value !== null && nonFieldErrors.value.length > 0) {
        addStep(props.nonFieldErrorStep);
    }
    if (fieldErrors.value !== null) {
        for (const fieldError of fieldErrors.value) {
            addStep(fieldError.step);
        }
    }

    if (stepsWithErrors.length > 0) {
        emit('visitStepsWithErrors', stepsWithErrors);
    }

    exceptionType.value = null;
    nonFieldErrors.value = null;
    fieldErrors.value = null;
    showModal.value = false;
}
</script>

<template>
    <v-dialog v-model="showModal" :max-width="640">
        <v-card>
            <v-card-title>
                {{ dialogTitle }}
            </v-card-title>
            <v-card-text>
                <template v-if="exceptionType !== null">
                    <v-alert type="error" variant="tonal">
                        <strong v-if="exceptionType === ExceptionType.NETWORK">
                            {{
                                pgettext('error', 'There was a problem submitting your data. Please check your internet connection and try again.')
                            }}
                        </strong>
                        <strong v-else>
                            {{
                                pgettext('error', 'We\'re sorry, something went wrong on our end. Please try again in a few moments.')
                            }}
                        </strong>
                    </v-alert>

                </template>
                <div v-else class="d-flex flex-column ga-4">
                    <v-alert v-for="(message, index) in nonFieldErrors" :key="index" type="error" variant="tonal">
                        <strong>{{ message }}</strong>
                    </v-alert>
                    <div v-for="error in fieldErrors">
                        <h2 class="mb-2 text-subtitle-1">
                            <strong>{{ error.label }}</strong>
                        </h2>

                        <div class="d-flex flex-column ga-3">
                            <v-alert v-for="(message, index) in error.messages" :key="index" type="error" variant="tonal">
                                <strong>{{ message }}</strong>
                            </v-alert>
                        </div>
                    </div>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-btn @click="confirm()">
                    <strong>{{ pgettext('action', 'Close') }}</strong>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
